import React, { useEffect } from 'react';
import { TitleBar } from './TitleBar';
import { useTranslation } from 'react-i18next';
import { Box } from '@chakra-ui/react';

interface PageLayoutProps {
  children: React.ReactNode;
  withPadding?: boolean;
  bgColor?: string;
  title?: string;
  showBackButton?: boolean;
  showRefreshButton?: boolean;
  customAction?: React.ReactNode;
  onRefresh?: () => void;
}

// 添加 iOS Safari viewport 修复
const useIOSViewportFix = () => {
  useEffect(() => {
    // 获取实际视口高度
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
};

/**
 * 通用页面布局组件，使用Tailwind进行样式设置
 * @param children 子组件
 * @param withPadding 是否添加内边距，默认为true
 * @param bgColor 背景色，默认为浅灰色（暗色模式下为深灰色）
 * @param title 页面标题，如果不提供则根据路由自动判断
 * @param showBackButton 是否显示返回按钮
 * @param showRefreshButton 是否显示刷新按钮
 * @param customAction 自定义操作按钮
 * @param onRefresh 自定义刷新函数
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  withPadding = true,
  title,
  showBackButton = false,
  showRefreshButton = true,
  customAction,
  onRefresh,
}) => {
  // 使用 iOS viewport 修复
  useIOSViewportFix();
  const { t } = useTranslation();

  return (
    // Use Chakra Box with as="main" to allow sx prop
    <Box 
      as="main"
      className={`flex flex-col`}
      style={{
        minHeight: 'calc(var(--vh, 1vh) * 100)', // Ensure it spans viewport vertically
        paddingTop: 'env(safe-area-inset-top)', 
        // Removed safe area bottom padding here, as content padding will handle it
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}
      sx={{ // Use sx prop for background color with Chakra UI
        bg: 'bg-primary', // Assuming bg-primary is defined in semanticTokens
        // Chakra UI handles dark mode automatically via semantic tokens
      }}
    >
      {/* TitleBar: sticky top */}
      <TitleBar 
        title={title}
        showBackButton={showBackButton}
        showRefreshButton={showRefreshButton}
        customAction={customAction}
        onRefresh={onRefresh}
      />
      {/* Content area wrapper: flex-grow, add padding-bottom for nav bar, allow scrolling */}
      <div 
        className={`flex-grow w-full max-w-full mx-auto pb-14 ${ // Added pb-14, removed overflow-hidden
          withPadding ? 'px-4 md:px-6' : '' // Horizontal padding only
        }`}
        // Removed fixed height style
      >
        {children} {/* Content like TaskList will render here and determine scroll height */}
      </div>
      {/* Navigation is rendered OUTSIDE this layout by PrivateRoute */} 
    </Box>
  );
};

export const ContentContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="w-full max-w-full px-4 md:px-6 mx-auto">
      {children}
    </div>
  );
};